import { useLang } from "@/context/LanguageContext";
import { ArrowRight, Play, Sparkles } from "lucide-react";
import { toast } from "sonner"; // kept for demo button

export default function Hero({ onOpenModal }) {
  const { lang, t } = useLang();

  const handlePrimary = () => {
    if (onOpenModal) onOpenModal();
  };

  const handleDemo = () => {
    toast.info(
      lang === "cs"
        ? "Ukázka: brzy přidáme interaktivní prohlídku."
        : "Showcase: an interactive tour is coming soon.",
    );
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Decorative blob */}
      <div
        aria-hidden
        className="absolute -top-24 -left-32 w-[420px] h-[420px] rounded-full bg-[#22C55E]/10 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute top-40 -right-32 w-[480px] h-[480px] rounded-full bg-[#F59E0B]/10 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#D1FAE5] shadow-sm"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#F59E0B]" />
            <span className="font-body text-[11px] uppercase tracking-widest font-bold text-[#047857]">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-[#064E3B] leading-[1.02]"
          >
            {t.hero.title_1}
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">{t.hero.title_2}</span>
              <span
                aria-hidden
                className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 bg-[#F59E0B]/40 -z-0 -rotate-1 rounded-full"
              />
            </span>
            <br />
            <span className="text-[#22C55E]">{t.hero.title_3}</span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 max-w-xl font-body text-lg md:text-xl text-[#047857] leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="hero-cta-primary"
              onClick={handlePrimary}
              className="group inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-[#22C55E] text-white font-body font-bold text-base shadow-xl shadow-green-900/20 hover:-translate-y-1 hover:shadow-2xl hover:bg-[#16A34A] transition-all duration-300"
            >
              {t.hero.cta_primary}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={handleDemo}
              className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-white border border-[#D1FAE5] text-[#064E3B] font-body font-semibold text-base hover:bg-[#FDFBF7] hover:-translate-y-1 transition-all duration-300"
            >
              <span className="w-7 h-7 rounded-full bg-[#064E3B] flex items-center justify-center">
                <Play className="w-3 h-3 text-white fill-white ml-0.5" />
              </span>
              {t.hero.cta_secondary}
            </button>
          </div>

          <p
            data-testid="hero-micro"
            className="mt-5 font-body text-sm text-[#047857]/80"
          >
            {t.hero.micro}
          </p>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-3 gap-4 md:gap-8 max-w-lg">
            {[
              { v: t.hero.stat_1, l: t.hero.stat_1_label },
              { v: t.hero.stat_2, l: t.hero.stat_2_label },
              { v: t.hero.stat_3, l: t.hero.stat_3_label },
            ].map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="font-heading text-2xl md:text-3xl font-black text-[#064E3B]">
                  {s.v}
                </div>
                <div className="font-body text-xs md:text-sm text-[#047857] mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: organic image collage */}
        <div className="lg:col-span-5 relative h-[460px] md:h-[560px]">
          {/* Big image */}
          <div
            data-testid="hero-image-primary"
            className="absolute top-0 right-0 w-[78%] h-[68%] overflow-hidden bg-[#D1FAE5]"
            style={{ borderRadius: "60% 40% 55% 45% / 50% 60% 40% 50%" }}
          >
            <img
              src="https://images.unsplash.com/photo-1759264244827-1dde5bee00a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBib3V0aXF1ZSUyMGhvdGVsJTIwaW50ZXJpb3IlMjBjb3p5fGVufDB8fHx8MTc3ODE2ODE3MHww&ixlib=rb-4.1.0&q=85"
              alt="Modern boutique hotel interior"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          {/* Smaller overlapping */}
          <div
            data-testid="hero-image-secondary"
            className="absolute bottom-0 left-0 w-[60%] h-[52%] overflow-hidden bg-white border-[6px] border-[#FDFBF7] shadow-2xl shadow-green-900/15"
            style={{ borderRadius: "45% 55% 60% 40% / 55% 45% 50% 50%" }}
          >
            <img
              src="https://images.unsplash.com/photo-1776763255122-3d35e32aee64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBib3V0aXF1ZSUyMGhvdGVsJTIwaW50ZXJpb3IlMjBjb3p5fGVufDB8fHx8MTc3ODE2ODE3MHww&ixlib=rb-4.1.0&q=85"
              alt="Cozy bed in boutique hotel room"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>

          {/* Floating booking pill */}
          <div
            data-testid="hero-floating-card"
            className="absolute top-12 -left-2 md:left-[-30px] bg-white rounded-2xl shadow-xl shadow-green-900/15 border border-[#D1FAE5] px-4 py-3 flex items-center gap-3 animate-[float_4s_ease-in-out_infinite]"
          >
            <div className="w-10 h-10 rounded-xl bg-[#22C55E]/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#22C55E]" />
            </div>
            <div>
              <div className="font-heading text-sm font-bold text-[#064E3B]">
                {lang === "cs" ? "Web spuštěn!" : "Website live!"}
              </div>
              <div className="font-body text-xs text-[#047857]">
                {lang === "cs" ? "Penzion Na Kopci · 3 dny" : "Na Kopci B&B · 3 days"}
              </div>
            </div>
          </div>

          {/* Star rating chip */}
          <div
            data-testid="hero-rating-card"
            className="absolute -bottom-2 right-2 md:right-6 bg-[#064E3B] text-white rounded-2xl px-4 py-3 shadow-xl flex items-center gap-2"
          >
            <span className="font-heading text-2xl font-black">4.9</span>
            <div className="flex flex-col text-[10px] leading-tight">
              <span className="text-[#F59E0B]">★★★★★</span>
              <span className="text-white/80">
                {lang === "cs" ? "1 240 hodnocení" : "1,240 reviews"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
